import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import { LuExternalLink } from "react-icons/lu";

import { cn } from "@/utils/lib";
import type { LinkPreviewApiResponse } from "@/utils/url-preview";
import {
  createEmptyUrlPreview,
  normalizeUrlPreviewTarget,
  URL_PREVIEW_CACHE_MAX_AGE_MS,
  URL_PREVIEW_QUERY_KEY,
} from "@/utils/url-preview";

type UrlPreviewProps = {
  url: string;
  compact: boolean;
};

export const UrlPreview = ({ url, compact }: UrlPreviewProps) => {
  const previewUrl = useMemo(() => {
    try {
      return normalizeUrlPreviewTarget(url);
    } catch {
      return null;
    }
  }, [url]);

  const { data } = useQuery({
    queryKey: [URL_PREVIEW_QUERY_KEY, previewUrl],
    enabled: !!previewUrl,
    queryFn: async (): Promise<LinkPreviewApiResponse> => {
      if (!previewUrl) {
        return createEmptyUrlPreview(url);
      }

      const searchParams = new URLSearchParams({
        url: previewUrl,
      });

      try {
        const res = await fetch(`/client-api/link-preview?${searchParams}`);

        if (!res.ok) {
          return createEmptyUrlPreview(previewUrl);
        }

        return await res.json();
      } catch {
        return createEmptyUrlPreview(previewUrl);
      }
    },
    meta: {
      suppressGlobalErrorToast: true,
    },
    retry: false,
    staleTime: Infinity,
    gcTime: URL_PREVIEW_CACHE_MAX_AGE_MS,
    refetchOnWindowFocus: false,
  });
  const domain = useMemo(() => {
    try {
      return new URL(previewUrl ?? url).hostname;
    } catch {
      return null;
    }
  }, [previewUrl, url]);

  if (!data?.title) {
    return null;
  }

  const { image, description, title } = data;

  return (
    <Link
      href={url}
      className={cn(
        `
          group relative flex flex-col overflow-hidden rounded-md border
          border-border-light bg-card
          hover:border-border
        `,
        {
          "flex-row items-center justify-start": compact,
          "items-start justify-center": !compact,
        },
      )}
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      {image && (
        <div
          className={cn("unforce-radius relative shrink-0 overflow-hidden", {
            "size-[68px]": compact,
            "aspect-video w-full": !compact,
          })}
        >
          <Image
            fill
            src={image}
            alt={title}
            className="
              unforce-radius object-cover transition-transform duration-300
              group-hover:scale-105
            "
          />
        </div>
      )}
      <LuExternalLink
        aria-hidden="true"
        className="pointer-events-none absolute top-2 right-2 size-3.5 text-muted"
      />
      <div
        className={cn("min-w-0 wrap-break-word", {
          "px-3 py-2 pr-8": compact,
          "p-4 pr-8": !compact,
        })}
      >
        {domain && (
          <span
            className={cn("flex text-foreground", {
              "text-[10px]/3": compact,
              "text-[11px]": !compact,
            })}
          >
            {domain}
          </span>
        )}
        <h3
          className={cn("line-clamp-1 font-semibold", {
            "pt-1 text-sm/4": compact,
            "text-base/6": !compact,
          })}
        >
          {title}
        </h3>
        {description && (
          <p
            className={cn("line-clamp-1 text-xs text-muted", {
              "pt-1 leading-4": compact,
              "pt-2": !compact,
            })}
          >
            {description}
          </p>
        )}
      </div>
    </Link>
  );
};
